<script setup lang="ts">
// Home/header mic control (round-19, 2026-08-31 — "we need the mic icon in the home screen so
// we can have it on/off and detect the voice or have some way to test the voice detection").
// Lives in KomboLogo.vue's right gutter, next to the Settings gear, so it shows on EVERY screen
// (Home reaches it through NavHeader -> KomboLogo, same as the clock) — same reasoning as those:
// one shared surface beats wiring a one-off icon into Home alone.
//
// Reuses the SHARED stt.ts client/state — no second WebSocket, per the single-client rule (only
// DebugStt.vue is exempt, as a raw tuning surface). This is a thin UI over
// stt.connected/stt.level/stt.lastFinal/stt.lastCommand plus store/settings.ts's
// voiceCommandsEnabled — the SAME field SettingsPanel.vue's switch flips, so the two controls are
// always in sync with zero extra plumbing.
//
// Tap = toggle voiceCommandsEnabled. Long-press = open a small popover with a live level bar +
// last transcript + last recognized command, so voice detection can be sanity-checked from Home
// without navigating to the Debug panel.
//
// Round-23 (2026-08-31): the pulse itself is MicPulse.vue (reusable, prop-driven on level/state)
// — this component is now just the tap/long-press affordance + popover wrapped around it.
import { computed, ref } from 'vue'
import { settings } from '../store/settings'
import { stt } from '../stt'
import MicPulse from './MicPulse.vue'

const LONG_PRESS_MS = 500
let pressTimer: ReturnType<typeof setTimeout> | null = null
let longPressed = false
const popoverOpen = ref(false)

function onPointerDown() {
  longPressed = false
  pressTimer = setTimeout(() => {
    longPressed = true
    popoverOpen.value = true
  }, LONG_PRESS_MS)
}
function onPointerUp() {
  if (pressTimer) clearTimeout(pressTimer)
  if (!longPressed) settings.voiceCommandsEnabled = !settings.voiceCommandsEnabled
}
function onPointerLeave() {
  if (pressTimer) clearTimeout(pressTimer)
}

// "broken" = on but not connected (kmb-stt unreachable/still reconnecting) — visually distinct
// from the deliberate off state, per the request to see when voice is "on/off" vs. actually
// working.
const state = computed<'off' | 'broken' | 'live'>(() => {
  if (!settings.voiceCommandsEnabled) return 'off'
  if (!stt.connected) return 'broken'
  return 'live'
})

// Round-23 (2026-08-31): the actual pulse rendering (rings + icon scale, driven by stt.level)
// moved to the standalone MicPulse.vue so Home can host a bigger, clearly-visible instance of
// the SAME pulse without duplicating the scale math — this button just wraps it with the
// existing tap/long-press affordance.
//
// Round-26 (2026-08-31): MicPulse now always renders a filled round "button" of its own (a real
// circle, not just an icon) — this wrapper no longer paints its own background (dropped
// `active:bg-neutral-800`, would double up as a square-ish tint behind MicPulse's circle); a
// slightly larger 24px size gives the header instance a touch more presence too.
</script>

<template>
  <div class="relative">
    <button
      type="button"
      class="tap-target w-9 h-9 min-h-0 min-w-0 shrink-0 rounded-full flex items-center justify-center"
      :aria-label="state === 'off' ? 'Voice commands off, tap to enable' : 'Voice commands on, tap to disable, hold to test'"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointerleave="onPointerLeave"
    >
      <MicPulse :level="stt.level" :state="state" :size="24" />
    </button>

    <div v-if="popoverOpen" class="fixed inset-0 z-[60]" @click="popoverOpen = false">
      <div class="absolute right-2 top-14 w-72 rounded-xl bg-neutral-900 border border-neutral-700 p-4 flex flex-col gap-3 shadow-2xl" @click.stop>
        <div class="flex items-center justify-between">
          <span class="font-semibold text-sm">Voice test</span>
          <span
            class="text-xs font-semibold px-2 py-0.5 rounded-full"
            :class="{
              'bg-neutral-700 text-neutral-300': state === 'off',
              'bg-red-700 text-white': state === 'broken',
              'bg-emerald-600 text-white': state === 'live',
            }"
          >
            {{ state === 'off' ? 'off' : state === 'broken' ? 'disconnected' : 'listening' }}
          </span>
        </div>

        <template v-if="state !== 'off'">
          <div class="flex items-center gap-2">
            <span class="text-xs text-neutral-500 font-semibold shrink-0 w-10">Level</span>
            <div class="flex-1 h-2.5 rounded-full bg-neutral-800 overflow-hidden">
              <div class="h-full bg-emerald-500 transition-all duration-75" :style="{ width: `${Math.min(100, stt.level * 300)}%` }" />
            </div>
          </div>
          <div class="text-xs text-neutral-400">
            <span class="text-neutral-600 font-semibold">Last heard: </span>
            <span v-if="stt.lastFinal?.text">"{{ stt.lastFinal.text }}"</span>
            <span v-else class="text-neutral-600">nothing yet — try speaking</span>
          </div>
          <div class="text-xs text-neutral-400">
            <span class="text-neutral-600 font-semibold">Last command: </span>
            <span v-if="stt.lastCommand">{{ stt.lastCommand.command }} ({{ (stt.lastCommand.confidence * 100).toFixed(0) }}%)</span>
            <span v-else class="text-neutral-600">none yet</span>
          </div>
        </template>
        <p v-else class="text-xs text-neutral-500">Voice commands are off — tap the mic icon to turn them on.</p>

        <button type="button" class="tap-target self-end px-4 rounded-lg bg-neutral-700 active:bg-neutral-600 text-sm font-semibold" @click="popoverOpen = false">
          Close
        </button>
      </div>
    </div>
  </div>
</template>
